import { useEffect, useRef, useState } from "react";

import { Typography } from "@mui/material";

import { fetchLinkedRecordIndices, isRecordIndexUnavailable } from "../../utils/exclusionCascade";

/**
 * Says how many of a claimant's linked rows the expand panel is NOT showing
 * (#6900 part A).
 *
 * ## The defect
 *
 * `getLinkedRecords` matches over the LOADED records. Measured 2026-09-08 on an
 * 8,000-record document, claimant `M0000` / member `50000000` has **7 rows** at
 * indices 0, 1200, 2400, 3600, 4800, 6000 and 7200; the tab loads 0-999, so the
 * panel showed **1**. Confirmed again by the reviewer on 2026-09-09:
 *
 *   "The panel for TESTER, M0000 only shows 1 item and doesn't give any
 *    indication that there are 6 other rows underneath it."
 *
 * Not a correctness bug since #6848 — the exclusion cascade resolves
 * whole-document at approve, so a wrong-looking panel no longer produces a
 * wrong outcome. But someone deciding whether a claimant looks right is
 * looking at this panel, and an undercounted claim list can make a claimant
 * look plausible when their full set would not.
 *
 * ## Why disclose rather than fetch the rows
 *
 * The linkage endpoint (#6848) already returns EVERY `record_index` for a
 * claimant, so its array length **is** the whole-document count — the
 * disclosure costs one request and no new backend. Fetching the missing rows
 * would need a new endpoint shape: the keyset read takes a RANGE, and a
 * claimant's rows are scattered (0, 1200, 2400 …), so seven rows would be seven
 * requests or a new set-of-indices endpoint.
 *
 * ## Why the fetch lives in this component
 *
 * It mounts only when a row is expanded, so the request is exactly one per
 * expand, on an explicit user action — bounded and attributable, which is
 * #6900's option 2. Putting the count in the chevron badge instead would fire
 * one request per visible ROW.
 */
export interface LinkedRecordsDisclosureProps {
  documentId?: string;
  transformationId?: string;
  /** The expanded record's absolute index — the linkage seed. */
  recordIndex?: number;
  /** How many linked rows the panel is showing. */
  shownCount: number;
  /**
   * REQUIRED. Whether more records exist than are loaded.
   *
   * When everything is loaded, `getLinkedRecords` searched the whole document
   * and the panel is already complete — so this must issue NO request (#6900
   * AC3, #6755 AC5). Required rather than defaulted because defaulting it true
   * would add a request to every expand on every small document, and
   * defaulting it false would silently disable the disclosure on the documents
   * that need it.
   */
  hasMoreRecords: boolean;
}

export const LinkedRecordsDisclosure = ({
  documentId,
  transformationId,
  recordIndex,
  shownCount,
  hasMoreRecords,
}: LinkedRecordsDisclosureProps) => {
  const [wholeDocumentCount, setWholeDocumentCount] = useState<number | null>(null);
  const [failed, setFailed] = useState(false);
  const cancelled = useRef(false);

  const ready = hasMoreRecords && !!documentId && !!transformationId && typeof recordIndex === "number";

  useEffect(() => {
    cancelled.current = false;
    if (!ready) return;

    void (async () => {
      try {
        const indices = await fetchLinkedRecordIndices(documentId!, transformationId!, [recordIndex!]);
        if (!cancelled.current) setWholeDocumentCount(indices.length);
      } catch (e) {
        if (cancelled.current) return;
        // 409 means the record index predates 000220, so the question cannot
        // be answered for this document at all. Treated the same as a
        // transient failure HERE because the remedy differs but the display
        // does not: either way we cannot state a number, and saying nothing is
        // better than guessing. The approve path distinguishes them, which is
        // where the distinction changes what the reviewer should do.
        void isRecordIndexUnavailable(e);
        setFailed(true);
      }
    })();

    return () => {
      cancelled.current = true;
    };
  }, [ready, documentId, transformationId, recordIndex]);

  // Everything is loaded, so the panel is already complete. Say nothing.
  if (!hasMoreRecords) return null;

  if (failed) {
    // Deliberately does NOT claim a number. The panel above shows what it
    // found; this says only that it may be incomplete, which is the honest
    // statement when the count is unavailable.
    return (
      <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
        This document is paged, so there may be more rows for this claimant than are shown.
      </Typography>
    );
  }

  // In flight. No placeholder count — a transient "1 of 1" would read as a
  // fact and is the wrong answer on exactly the documents this exists for.
  if (wholeDocumentCount === null) return null;

  // The panel is complete after all: every linked row happened to be loaded.
  if (wholeDocumentCount <= shownCount) return null;

  return (
    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
      Showing {shownCount.toLocaleString()} of {wholeDocumentCount.toLocaleString()} rows for this claimant. Load more
      records to see the rest.
    </Typography>
  );
};
